import React, { useState, useEffect, useRef, useMemo, ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Modal,
  Portal,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ReactToPdf from 'react-to-pdf';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import RemoveCircleRoundedIcon from '@mui/icons-material/RemoveCircleRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { KpiPercentileType, ReportTypes, UserReportAccessTypes } from '../../propTypes';
import {
  COLOR_GREEN,
  COLOR_PRIMARY,
  COLOR_RED,
  COLOR_TOOLTIP_BACKGROUND,
  exchangeRates,
  monthNames,
} from 'src/const';
import { defaultKpiDisplaySchema, displayNameToKeyDict, KpiDetails } from './kpiSchema';
import { InfoButton } from 'src/App/Components/Buttons';
import {
  calculateHistoricalTrends,
  formatCurrency,
  getDummyMarketURL,
  getSeasonality,
  getSeasonalityBarColor,
  mapOptions,
  parseMessageHistoricalData,
} from 'src/utils/functions';
import RearViewKeyMetrics from 'src/App/HOC/Cards/KeyMetricsCard/RearViewKeyMetrics';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RepotPageInputSection from './Components/ReportPageInputSection';

import { reportPageStyles as styles } from './styles';
import EditButton from '../../../../Components/Buttons/EditButton';
import ReportPageMapSection from './Components/ReportPageMapSection';
import ReportPageCalculatorSection from './Components/ReportPageCalculatorSection';
import { COLOR_GREY_600, COLOR_PRIMARY_1 } from 'src/colors';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { User } from 'src/interfaces/iuser';
import TooltipNew from '../../../../HOC/Tooltips/TooltipNew';
import ShareLinkModal from '../../../../Components/Modals/ShareLinkModal';
import { checkIfUserAllowedToEditReport, getIfReportTypeDisabled } from '../../helper';
import { useHistory } from 'react-router-dom';
import { callApi } from 'src/core/callApi';
import { getHistoricalData } from 'src/App/Pages/OverviewPage/helper';
import { logAmplitudeEvent } from 'src/utils/analytics';

const ReportPageRightActionSection = ({
  urlPath,
  handleCreateNewReport,
  matches,
  user,
  handleLoginOrPricingModalOpen,
  isUserReportOwner,
  setComingSoonModalOpen,
  pdfRef,
  isPdfDownloadTriggerd,
  setIsPdfDownloadTriggerd,
  reportId,
}: {
  urlPath: string;
  handleCreateNewReport: () => void;
  matches: boolean;
  user: User;
  handleLoginOrPricingModalOpen: (state: 'open' | 'close', forceOpen?: 'payment' | null) => void;
  isUserReportOwner: boolean;
  setComingSoonModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  pdfRef;
  isPdfDownloadTriggerd: boolean;
  setIsPdfDownloadTriggerd: React.Dispatch<React.SetStateAction<boolean>>;
  reportId: string;
}) => {
  const [copyLinkModalOpen, setCopyLinkModalOpen] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  const isUserHavingCredits: boolean = user.estimatorCredits > 0;
  const isUserSubscribed: boolean = user.subscriptionPlan != 'starter';

  const history = useHistory();

  const downloadPdf = async toPdf => {
    setIsPdfDownloadTriggerd(true);
    setTimeout(async () => {
      await toPdf();
      setIsPdfDownloadTriggerd(false);
    }, 100);
  };

  return (
    <>
      <Grid item container justifyContent="flex-end" sx={{ ...styles.paper, width: 'auto' }}>
        <Grid item>
          <Button
            startIcon={<AddRoundedIcon sx={{ fontSize: '18px' }} />}
            sx={{
              ...styles.actionSectionButton,
              width: isUserHavingCredits ? '180px' : 'min(300px, 95%)',
              ...(matches ? { fontSize: '14px' } : {}),
            }}
            onClick={() => handleCreateNewReport()}
          >
            New Report
          </Button>
        </Grid>

        <Grid
          item
          id="share-download-menu-anchor"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button onClick={() => setIsMenuOpen(true)}>
            <MoreVertIcon sx={{ color: COLOR_GREY_600 }} />
          </Button>
          <Menu
            id="lock-menu"
            anchorEl={document.getElementById('share-download-menu-anchor')}
            open={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
          >
            <ReactToPdf
              targetRef={pdfRef}
              x={10}
              y={10}
              scale={1}
              options={{
                format: [210, 600],
              }}
              filename={`report-${reportId}`}
            >
              {({ toPdf }) => (
                <MenuItem>
                  <Button
                    startIcon={
                      isPdfDownloadTriggerd ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CloudDownloadOutlinedIcon sx={{ marginRight: '10px' }} />
                      )
                    }
                    sx={{
                      ...styles.actionSectionButton,
                      backgroundColor: '#fff',
                      color: '#000',
                      justifyContent: 'flex-start',
                      py: 0,
                      '&:hover': {
                        backgroundColor: '#fff',
                      },
                    }}
                    onClick={() => downloadPdf(toPdf)}
                  >
                    Download
                  </Button>
                </MenuItem>
              )}
            </ReactToPdf>

            <MenuItem>
              <Button
                startIcon={
                  <ReplyRoundedIcon sx={{ transform: 'scaleX(-1)', marginRight: '10px' }} />
                }
                sx={{
                  ...styles.actionSectionButton,
                  backgroundColor: '#fff',
                  color: '#000',
                  justifyContent: 'flex-start',
                  py: 0,
                  '&:hover': {
                    backgroundColor: '#fff',
                  },
                }}
                onClick={() => {
                  setCopyLinkModalOpen(true);
                  logAmplitudeEvent('report_shared', { source: 'report_page' });
                }}
              >
                Share
              </Button>
            </MenuItem>

            <MenuItem>
              <Button
                startIcon={<AnalyticsIcon sx={{ marginRight: '10px' }} />}
                sx={{
                  ...styles.actionSectionButton,
                  backgroundColor: '#fff',
                  color: '#000',
                  justifyContent: 'flex-start',
                  py: 0,
                  '&:hover': {
                    backgroundColor: '#fff',
                  },
                }}
                onClick={() => history.push('/airbnb-calculator')}
              >
                My Reports
              </Button>
            </MenuItem>
          </Menu>
        </Grid>
      </Grid>

      <ShareLinkModal
        open={copyLinkModalOpen}
        setOpen={setCopyLinkModalOpen}
        shareUrl={`https://app.airbtics.com/${urlPath}`}
      />
    </>
  );
};

const ReportPageKPISection = ({
  reportType,
  reportKPIData,
  currency,
  selectedKpiPercentile,
  setSelectedKpiPercentile,
  isUserReportOwner,
  trackEditOpened,
  trackEditPerformed,
  isOnboardingComplete,
  isUserSubscribed,
  handleLoginOrPricingModalOpen,
  reportAccessType,
  seasonality,
  trend,
  nearestMarket,
  nearestMarketURL,
}: {
  reportType: ReportTypes;
  reportKPIData: any;
  currency: string;
  selectedKpiPercentile: KpiPercentileType;
  setSelectedKpiPercentile: React.Dispatch<React.SetStateAction<KpiPercentileType>>;
  isUserReportOwner: boolean;
  trackEditOpened: (editType: any) => void;
  trackEditPerformed: (editType: any) => void;
  isOnboardingComplete: boolean;
  isUserSubscribed: boolean;
  handleLoginOrPricingModalOpen: (state: 'open' | 'close', forceOpen?: 'payment' | null) => void;
  reportAccessType: UserReportAccessTypes;
  seasonality: number | null;
  trend: number | null;
  nearestMarket: any;
  nearestMarketURL: string | null;
}) => {
  const [kpiDisplaySchema, setKpiDisplaySchema] = useState<any>(
    defaultKpiDisplaySchema[reportType]
  );
  const [selectedKpi, setSelectedKpi] = useState<
    {
      name: string;
      tooltip: string;
      value: number;
      unitType: 'currency' | 'percentage';
    }[]
  >([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [timePeriod, setTimePeriod] = useState(12);
  const [grapgDataArray, setGraphDataArray] = useState<any[]>([]);

  const getKeyFromDisplayName = (displayName: string) => {
    return displayNameToKeyDict[displayName];
  };

  useEffect(() => {
    const loadDefaultKpis = () => {
      const kpis = [...kpiDisplaySchema];

      const selectedKpiLists: {
        name: string;
        tooltip: string;
        value: number;
        unitType: 'currency' | 'percentage';
      }[] = [];

      kpis.forEach((kpi: any) => {
        const kpiKey = Object.keys(kpi)[0];
        const kpiKeyApi = getKeyFromDisplayName(kpiKey);
        const kpiDisplayStatus = kpi[kpiKey];

        const tooltip =
          nearestMarket && nearestMarketURL ? (
            kpiKeyApi == 'seasonality' ? (
              <>
                <Typography sx={{ mb: 1 }}>
                  Seasonality reflects how revenue changes throughout the year by comparing each
                  month's average revenue to the overall yearly average.
                </Typography>
                <Typography>
                  This shows the seasonality of{' '}
                  <Link href={nearestMarketURL as string} target="_blank">
                    {nearestMarket?.district}
                  </Link>
                  , the nearest available city to your address.
                </Typography>
              </>
            ) : kpiKeyApi == 'trend' ? (
              <>
                <Typography sx={{ mb: 1 }}>
                  It is the predicted value of year-over-year changes in revenue based on trends
                  observed from 2022 to 2024.
                </Typography>
                <Typography>
                  This shows the trend of{' '}
                  <Link href={nearestMarketURL as string} target="_blank">
                    {nearestMarket?.district}
                  </Link>
                  , the nearest available city to your address.
                </Typography>
              </>
            ) : (
              KpiDetails[kpiKey].tooltip
            )
          ) : (
            KpiDetails[kpiKey].tooltip
          );

        const unitType = KpiDetails[kpiKey].unitType;
        const value =
          kpiKeyApi == 'seasonality'
            ? getSeasonalityBarColor(seasonality, true)
            : kpiKeyApi == 'trend'
            ? trend
            : reportKPIData[selectedKpiPercentile][kpiKeyApi];

        kpiDisplayStatus === 'visible' &&
          selectedKpiLists.push({
            name: kpiKey,
            tooltip: tooltip,
            value:
              (kpiKeyApi == 'seasonality' || kpiKeyApi == 'trend') && !value ? (
                <>
                  Unavailable, check{' '}
                  <Link href={window.location.origin} target="_blank">
                    Overview
                  </Link>
                </>
              ) : (
                value
              ),
            unitType: unitType,
          });
      });

      setSelectedKpi(selectedKpiLists);
      setGraphDataArray(getSessionilityGraphData());
    };

    loadDefaultKpis();
  }, [kpiDisplaySchema, selectedKpiPercentile, nearestMarket]);

  const getKpiCard = (
    kpiName: string,
    value: number | React.ReactElement,
    tooltip: string | React.ReactElement,
    unitType: 'currency' | 'percentage'
  ) => {
    return (
      <Grid
        container
        item
        xs={6}
        justifyContent={'center'}
        alignItems={'center'}
        sx={{ padding: '5px' }}
      >
        <Grid
          container
          item
          xs={12}
          justifyContent={'center'}
          alignContent={'center'}
          alignItems={'center'}
          sx={{ ...styles.paper, height: '140px', padding: '20px' }}
        >
          <>
            <Grid item xs={12} alignItems={'center'} sx={{ marginBottom: '10px' }}>
              <Typography variant="body1" sx={{ fontSize: '16px' }}>
                {kpiName}{' '}
                <InfoButton
                  tooltipContent={
                    <Card>
                      <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                        <Grid container direction="column" alignItems="flex-start">
                          <Typography variant="body1" sx={{ fontWeight: 400 }}>
                            {tooltip}
                          </Typography>
                        </Grid>
                      </CardContent>
                    </Card>
                  }
                />
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: COLOR_PRIMARY }}>
                {unitType === 'currency' && exchangeRates[currency].symbol}
                {unitType === 'currency' && typeof value == 'number'
                  ? formatCurrency(String(value))
                  : value}
                {unitType === 'percentage' && typeof value == 'number' && '%'}
              </Typography>
            </Grid>
          </>
        </Grid>
      </Grid>
    );
  };

  const checkIfKpiVisible = (targetKpiName: string) => {
    let kpiVisible: boolean = false;

    const kpiArray = kpiDisplaySchema;

    kpiArray.forEach((kpi: any) => {
      const kpiName = Object.keys(kpi)[0];
      if (targetKpiName === kpiName && kpi[kpiName] === 'visible') {
        kpiVisible = true;
      }
    });

    return kpiVisible;
  };

  const handleKpiEdit = (target_kpi: string, action: 'add' | 'remove') => {
    const kpis = [...kpiDisplaySchema];

    if (!kpis) return;

    kpis.forEach((kpi: any) => {
      const kpiKey = Object.keys(kpi)[0];

      if (kpiKey === target_kpi) {
        kpi[kpiKey] = action === 'add' ? 'visible' : 'hidden';
      }
    });

    setKpiDisplaySchema(kpis);

    logAmplitudeEvent('report_edited', { edit_type: 'kpi' });
  };

  const getKpiEditLists = () => {
    const kpis = kpiDisplaySchema;

    const selectedKpiLists: {
      name: string;
      isVisible: boolean;
    }[] = [];

    kpis.forEach((kpi: any) => {
      const kpiKey = Object.keys(kpi)[0];

      selectedKpiLists.push({
        name: kpiKey,
        isVisible: checkIfKpiVisible(kpiKey),
      });
    });

    return (
      <>
        {selectedKpiLists.map(kpi => (
          <Grid
            container
            item
            justifyContent={'space-between'}
            alignItems={'center'}
            xs={12}
            sx={{
              marginBottom: '10px',
              backgroundColor: '#F3F6FF',
              borderRadius: '900px',
              padding: '5px 30px',
            }}
          >
            <Grid item xs={10}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {kpi.name}
              </Typography>
            </Grid>
            <Grid container item xs={2} direction={'row-reverse'}>
              <IconButton>
                {!kpi.isVisible ? (
                  <AddCircleRoundedIcon
                    sx={{ color: COLOR_GREEN }}
                    onClick={() => {
                      handleKpiEdit(kpi.name, 'add');
                    }}
                  />
                ) : (
                  <RemoveCircleRoundedIcon
                    sx={{ color: COLOR_RED }}
                    onClick={() => {
                      handleKpiEdit(kpi.name, 'remove');
                    }}
                  />
                )}
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </>
    );
  };

  const getSessionilityGraphData = () => {
    const graphDataObject = {};

    // initialize the graphDataObject with timePeriod
    const monthlyRevenueObject = reportKPIData[50].monthly_revenue;
    Object.keys(monthlyRevenueObject).forEach(date => {
      graphDataObject[date] = {};
    });

    [25, 50, 75, 90].map((percentile: number) => {
      const monthlyRevenueObject = reportKPIData[percentile].monthly_revenue;

      Object.keys(monthlyRevenueObject).forEach((date: any) => {
        graphDataObject[date][percentile] = monthlyRevenueObject[date];
      });
    });

    const graphData: {
      timePeriod: string;
      date: string;
      Revenue: number;
      '0.25': number;
      '0.5': number;
      '0.75': number;
    }[] = [];

    Object.keys(graphDataObject).forEach(date => {
      const [year, month] = date.split('-');
      const timePeriod = monthNames[parseInt(month) - 1] + ', ' + year;

      const data = {
        timePeriod: timePeriod,
        date: date,
        Revenue: graphDataObject[date][50],
        '0.25': graphDataObject[date][25],
        '0.5': graphDataObject[date][50],
        '0.75': graphDataObject[date][75],
        '0.9': graphDataObject[date][90],
      };

      graphData.push(data);
    });

    return graphData;
  };

  return (
    <>
      <Grid
        item
        container
        alignItems="flex-start"
        justifyContent="center"
        xs
        sx={{ ...styles.paper, ...styles.reportKPISection }}
      >
        <Grid
          container
          item
          xs={12}
          direction={'row-reverse'}
          sx={{ marginTop: '-10px', marginBottom: '10px' }}
        >
          <EditButton
            isDisabled={!isUserReportOwner}
            actionOnClick={() => {
              setIsEditModalOpen(true);
              trackEditOpened('kpi');
            }}
            isLocked={false}
          />
        </Grid>
        <>{selectedKpi.map(kpi => getKpiCard(kpi.name, kpi.value, kpi.tooltip, kpi.unitType))}</>
        <Grid
          container
          item
          xs={12}
          justifyContent={'center'}
          sx={{ marginTop: '20px', ...styles.paper, padding: 0 }}
        >
          {grapgDataArray.length && (
            <RearViewKeyMetrics
              title={'Revenue'}
              graphData={grapgDataArray}
              aggregateData={reportKPIData[selectedKpiPercentile].ltm_revenue}
              tooltip={{
                title: 'Revenue',
                content: [
                  `What’s the median revenue a property in this market generated over the last 12 months?`,
                  '',
                  `This Revenue figure represents the median over the past 12 months and does not include taxes, service fees, or additional guest fees.`,
                ],
              }}
              color={COLOR_PRIMARY}
              isDisabled={getIfReportTypeDisabled(reportAccessType, 'RevenueLTMGraph')}
              currency={currency}
              unit={exchangeRates[currency].symbol}
              metricsKey={'Revenue'}
              percentile={Number(selectedKpiPercentile) / 100}
              timePeriod={timePeriod}
              setPercentile={
                setSelectedKpiPercentile as React.Dispatch<React.SetStateAction<string>>
              }
              setTimePeriod={setTimePeriod}
              onDisabledOptionClick={() =>
                isUserReportOwner && handleLoginOrPricingModalOpen('open', 'payment')
              }
            />
          )}
        </Grid>
      </Grid>

      <Modal
        open={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          trackEditPerformed('kpi');
        }}
      >
        <Box
          sx={{
            ...styles.paper,
            ...styles.modalCenter,
            width: 'clamp(300px, 70%, 600px)',
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
          <Grid container>
            <Grid container item xs={12}>
              <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '20px' }}>
                KPIs
              </Typography>
              {getKpiEditLists()}
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

const ReportPage = ({
  reportData,
  currency,
  handleCreateNewReport,
  handleReport,
  matches,
  user,
  handleLoginOrPricingModalOpen,
  setComingSoonModalOpen,
  dummyOverviewMarket,
  loginCountryCode,
}: {
  reportData: any;
  currency: string;
  handleCreateNewReport: () => void;
  handleReport: (method: any, requestBody: null | any, id?: string) => Promise<void>;
  matches: boolean;
  user: User;
  handleLoginOrPricingModalOpen: (state: 'open' | 'close', forceOpen?: 'payment' | null) => void;
  setComingSoonModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dummyOverviewMarket: any;
  loginCountryCode: string;
}) => {
  const isUserReportOwner: boolean = checkIfUserAllowedToEditReport(
    user.email,
    reportData.primary_input?.created_by
  );

  const [selectedKpiPercentile, setSelectedKpiPercentile] = useState<KpiPercentileType>('50');
  const [openedEdits, setOpenedEdits] = useState(new Set());
  const [editCounts, setEditCounts] = useState<{ [key: string]: number }>({});
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
  const [nearestMarket, setNearestMarket] = useState<any>(null);
  const [nearestMarketURL, setNearestMarketURL] = useState<string | null>(null);
  const [seasonality, setSeasonality] = useState<number | null>(null);
  const [trend, setTrend] = useState<number | null>(null);

  const pdfRef = useRef(null);
  const [isPdfDownloadTriggerd, setIsPdfDownloadTriggerd] = useState<boolean>(false);

  useEffect(() => {
    const getNearestMarket = async reportID => {
      const url =
        'https://a1rd9gu3ea.execute-api.us-east-2.amazonaws.com/default/getNearestReportMarket';
      try {
        const response = await callApi(url, { queryParams: { id: reportID } }, false);
        if (response.message) {
          setNearestMarket(response.message);
          const data = response.message;
          const district = data['district'];
          const city = data?.city || '0';
          const country = data?.country ? data?.country.replace(' ', '-') : '0';
          const nearestMarketURL = `${window.location.origin}/airbnb-data/${country}/${city}/${district}`.toLowerCase();
          setNearestMarketURL(nearestMarketURL);
        }
      } catch (e) {
        console.error('Failed to fetch nearest market: ', e);
        setNearestMarket(null);
      }
    };
    const reportID = reportData.primary_input.id;
    getNearestMarket(reportID);
  }, [reportData]);

  useEffect(() => {
    if (
      !nearestMarket ||
      !nearestMarket.cached_data ||
      !nearestMarket.cached_data.historical_monthly_performance
    ) {
      return;
    }

    try {
      const cachedMarketData = nearestMarket.cached_data;
      const parsedCachedMarketData = parseMessageHistoricalData(cachedMarketData);

      const [revenueHistoricalData, revenueYoYChange] = getHistoricalData(
        parsedCachedMarketData.historical_monthly_performance,
        'revenue'
      );

      const seasonilityData = getSeasonality(
        revenueHistoricalData,
        'overview',
        selectedKpiPercentile
      );

      const { yearlyYoYChange, nextYearPredcition } = calculateHistoricalTrends(
        revenueHistoricalData,
        'revenue',
        selectedKpiPercentile
      );

      setSeasonality(seasonilityData.seasonality);
      setTrend(nextYearPredcition);
    } catch (e) {
      console.error('Failed to set seasonality and trend from nearest market in report', e);
    }
  }, [nearestMarket, selectedKpiPercentile]);

  const isUserSubscribed: boolean = user.subscriptionPlan != 'starter';

  const reportAccessType: UserReportAccessTypes = reportData.primary_input?.user_report_access_type;

  useEffect(() => {
    const savedOnboardingState = localStorage.getItem('user-onboarding-state');
    if (savedOnboardingState) {
      const savedOnboardingStateJSON = JSON.parse(savedOnboardingState);
      setOpenedEdits(new Set(savedOnboardingStateJSON.openedEdits));
      setEditCounts(savedOnboardingStateJSON.editCounts);
      checkOnboardingCompletion();
    }
  }, []);

  useEffect(() => {
    // persisting state across reloads
    const editCountSum = Object.values(editCounts).reduce((acc, value) => acc + value, 0);
    if (openedEdits.size == 0 && editCountSum == 0) {
      return;
    }
    localStorage.setItem(
      'user-onboarding-state',
      JSON.stringify({
        openedEdits: Array.from(openedEdits),
        editCounts,
      })
    );
    checkOnboardingCompletion();
  }, [openedEdits, editCounts]);

  useEffect(() => {
    if (isOnboardingComplete && !isUserSubscribed) {
      const timer = setTimeout(() => {
        handleLoginOrPricingModalOpen('open', 'payment');
      }, 3000);
      return () => clearTimeout(timer);
    }
    return;
  }, [isOnboardingComplete]);

  if (!reportData) {
    return <Typography variant="h4">Loading...</Typography>;
  }

  const reportType: ReportTypes = reportData.primary_input.report_type;

  const getCurrentPropertyOrRentPrice = () => {
    return reportData.fincal_input.property_price
      ? reportData.fincal_input.property_price
      : reportData.fincal_input.monthly_rent
      ? reportData.fincal_input.monthly_rent
      : null;
  };

  const checkOnboardingCompletion = () => {
    const hasOpenedTwoEdits = openedEdits.size >= 2;
    const hasEditedTwice = Object.values(editCounts).reduce((acc, count) => acc + count, 0) >= 2;
    // console.log('opened 2 edits', hasOpenedTwoEdits, 'edited twice', hasEditedTwice);

    if (hasOpenedTwoEdits || hasEditedTwice) {
      // console.log('Onboarding complete');
      setIsOnboardingComplete(true);
    } else {
      // console.log('Onboarding not yet complete');
      setIsOnboardingComplete(false);
    }
  };

  const trackEditOpened = editType => {
    setOpenedEdits(prevOpenedEdits => {
      const newOpenedEdits = new Set(prevOpenedEdits);
      newOpenedEdits.add(editType);
      return newOpenedEdits;
    });

    if (!editCounts[editType]) {
      setEditCounts(prevEditCounts => ({
        ...prevEditCounts,
        [editType]: 0,
      }));
    }
  };

  const trackEditPerformed = editType => {
    setEditCounts(prevEditCounts => ({
      ...prevEditCounts,
      [editType]: (prevEditCounts[editType] || 0) + 1,
    }));
  };

  // UI Elements

  const ReportPageMapSectionElement = (
    <Grid container item xs={12}>
      <ReportPageMapSection
        reportId={reportData.primary_input.id}
        lat={reportData.primary_input.latitude}
        lng={reportData.primary_input.longitude}
        radius={reportData.primary_input.radius}
        comps={reportData.comps}
        currency={currency}
        handleReport={handleReport}
        isUserReportOwner={isUserReportOwner}
        trackEditOpened={trackEditOpened}
        trackEditPerformed={trackEditPerformed}
        isOnboardingComplete={isOnboardingComplete}
        isUserSubscribed={isUserSubscribed}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
        reportAccessType={reportAccessType}
      />
    </Grid>
  );

  const CreateReportActionButtonElement = (
    <Grid container item xs={12} flexDirection={'row'}>
      <ReportPageRightActionSection
        urlPath={reportData.primary_input.url_path}
        handleCreateNewReport={handleCreateNewReport}
        matches={matches}
        user={user}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
        isUserReportOwner={isUserReportOwner}
        setComingSoonModalOpen={setComingSoonModalOpen}
        pdfRef={pdfRef}
        isPdfDownloadTriggerd={isPdfDownloadTriggerd}
        setIsPdfDownloadTriggerd={setIsPdfDownloadTriggerd}
        reportId={reportData.primary_input.id}
      />
    </Grid>
  );

  const InputSectionElement = (
    <Grid container item xs={12}>
      <RepotPageInputSection
        reportInputs={reportData.primary_input}
        currentPropertyPriceOrRentPrice={getCurrentPropertyOrRentPrice()}
        handleReport={handleReport}
        currency={currency}
        isUserReportOwner={isUserReportOwner}
        trackEditOpened={trackEditOpened}
        trackEditPerformed={trackEditPerformed}
        isOnboardingComplete={isOnboardingComplete}
        isUserSubscribed={isUserSubscribed}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
        loginCountryCode={loginCountryCode}
      />
    </Grid>
  );

  const KpiSectionElement = (
    <Grid container item xs={12}>
      <ReportPageKPISection
        reportType={reportType}
        reportKPIData={reportData.kpi}
        currency={currency}
        selectedKpiPercentile={selectedKpiPercentile}
        setSelectedKpiPercentile={setSelectedKpiPercentile}
        isUserReportOwner={isUserReportOwner}
        trackEditOpened={trackEditOpened}
        trackEditPerformed={trackEditPerformed}
        isOnboardingComplete={isOnboardingComplete}
        isUserSubscribed={isUserSubscribed}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
        reportAccessType={reportAccessType}
        seasonality={seasonality}
        trend={trend}
        nearestMarket={nearestMarket}
        nearestMarketURL={nearestMarketURL}
      />
    </Grid>
  );

  const CalculatorSectionElement = (
    <Grid container item xs={12}>
      <ReportPageCalculatorSection
        currency={currency}
        reportType={reportType}
        reportId={reportData.primary_input.id}
        reportCalculatorInputs={reportData.fincal_input}
        revenueProjection={reportData.kpi[selectedKpiPercentile]['ltm_revenue']}
        handleReport={handleReport}
        reportKPIData={reportData.kpi[selectedKpiPercentile]}
        isUserReportOwner={isUserReportOwner}
        trackEditOpened={trackEditOpened}
        trackEditPerformed={trackEditPerformed}
        isOnboardingComplete={isOnboardingComplete}
        isUserSubscribed={isUserSubscribed}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
      />
    </Grid>
  );

  const DemoMarketStatisticButton = (
    <Grid container item xs={12} justifyContent="center">
      <Grid
        container
        item
        xs={12}
        justifyContent={'center'}
        alignContent={'center'}
        alignItems={'center'}
        sx={{ ...styles.paper, height: '80px', padding: '20px' }}
      >
        <Button
          variant="outlined"
          sx={{ color: COLOR_PRIMARY, borderColor: COLOR_PRIMARY, textTransform: 'none' }}
          onClick={() => {
            window.open(
              nearestMarketURL ? nearestMarketURL : getDummyMarketURL(dummyOverviewMarket),
              '_self'
            );
          }}
        >
          Go to market statistics
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <>
      {!matches ? (
        // DESKTOP UI
        <Grid
          container
          xs
          sx={{ background: '#F3FAFB', padding: '20px', paddingTop: '40px' }}
          spacing={4}
        >
          {/* Left Section */}
          <Grid container item xs={12} md={6} spacing={2}>
            {CreateReportActionButtonElement}
            {InputSectionElement}
            {KpiSectionElement}
          </Grid>
          {/* Right Section */}
          <Grid container item xs={12} md={6} spacing={4} alignContent="flex-start">
            {ReportPageMapSectionElement}
            {CalculatorSectionElement}
            {DemoMarketStatisticButton}
          </Grid>
        </Grid>
      ) : (
        // MOBILE UI
        <Grid container xs sx={{ background: '#F3FAFB', padding: '20px' }} rowSpacing={2}>
          {CreateReportActionButtonElement}
          {InputSectionElement}
          {ReportPageMapSectionElement}
          {KpiSectionElement}
          {CalculatorSectionElement}
          {DemoMarketStatisticButton}          
        </Grid>
      )}

      {isPdfDownloadTriggerd && (
        <Grid
          container
          xs
          sx={{
            background: '#F3FAFB',
            top: '-10000px',
            width: '710px',
            minWidth: '710px',
            paddingRight: '40px',
            paddingBottom: '40px',
          }}
          spacing={4}
          ref={pdfRef}
        >
          {InputSectionElement}
          {KpiSectionElement}
          {CalculatorSectionElement}
          <Grid xs={12} container justifyContent={'center'} alignItems={'center'}>
            <Typography variant='body2' sx={{my: '20px'}}>
              Report generated by <span style={{ color: COLOR_PRIMARY_1 }}>airbtics.com</span> –
              Accurate Airbnb Analytics.
            </Typography>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default React.memo(ReportPage);
